import { useState, useRef } from "react";
import { Upload, FileText, X, Loader2, CheckCircle, Ruler, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALLOWED_EXTENSIONS = [".ai", ".eps", ".pdf", ".svg", ".jpg", ".jpeg"];

export const MyDesignUpload = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    description: "",
    width: "",
    height: "",
    unit: "mm",
    quantity: 1,
    notes: ""
  });

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file extension
    const ext = "." + selectedFile.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error(`Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(", ")}`);
      return;
    }

    // Validate file size (50MB max)
    if (selectedFile.size > 50 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 50MB");
      return;
    }

    setFile(selectedFile);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      toast.error("Please select a design file");
      return;
    }

    if (!formData.name || !formData.email || !formData.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    setUploading(true);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);

      // Build query params
      const params = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || "",
        description: formData.description,
        width: formData.width || "",
        height: formData.height || "",
        unit: formData.unit,
        quantity: formData.quantity.toString(),
        notes: formData.notes || ""
      });

      const response = await fetch(`${API}/designs/upload?${params}`, {
        method: "POST",
        body: uploadFormData
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success("Design submitted successfully! We'll be in touch soon.");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to upload design");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload design. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setSubmitted(false);
    setFormData({
      name: "",
      email: "",
      phone: "",
      description: "",
      width: "",
      height: "",
      unit: "mm",
      quantity: 1,
      notes: ""
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-3xl p-8 shadow-sm text-center" data-testid="design-upload-success">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>
          Design Submitted!
        </h3>
        <p className="text-gray-600 mb-6">
          Thank you for uploading your design. We'll review it and get back to you with a quote shortly.
        </p>
        <Button
          onClick={resetForm}
          variant="outline"
          className="rounded-full px-6"
          data-testid="submit-another-btn"
        >
          Submit Another Design
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm" data-testid="design-upload-section">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-black rounded-full mb-4">
          <Upload className="h-7 w-7 text-white" />
        </div>
        <h2 
          className="text-2xl md:text-3xl font-bold mb-3"
          style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
        >
          Upload Your Artwork
        </h2>
        <p 
          className="text-lg font-medium text-gray-800 mb-3"
          style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
        >
          Turn Your Design Into Custom Stripes or Decals
        </p>
        <div className="text-gray-600 text-sm leading-relaxed max-w-lg mx-auto space-y-3">
          <p>
            Have your own artwork or logo? Simply upload your file and we can transform it into high‑quality custom stripes or decals made to your exact requirements. Whether it's a personal design, business branding, or something completely unique, we'll prepare it for production using professional automotive‑grade materials.
          </p>
          <p>
            Just upload your artwork, add any notes or preferences, and we'll take care of the rest.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* File Upload Area */}
        <div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".ai,.eps,.pdf,.svg,.jpg,.jpeg"
            className="hidden"
            data-testid="design-file-input"
          />
          
          {!file ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center cursor-pointer hover:border-black hover:bg-gray-50 transition-all"
              data-testid="design-dropzone"
            >
              <FileText className="h-10 w-10 text-gray-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-700 mb-1">
                Click to upload your design file
              </p>
              <p className="text-xs text-gray-500">
                AI, EPS, PDF, SVG, JPEG (max 50MB)
              </p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-2xl p-4 flex items-center gap-4" data-testid="design-file-preview">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-gray-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                <p className="text-xs text-gray-500">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                data-testid="remove-file-btn"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>

        {/* Contact Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              placeholder="Your name"
              required
              data-testid="design-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              placeholder="your@email.com"
              required
              data-testid="design-email-input"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone (optional)
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            placeholder="Your phone number"
            data-testid="design-phone-input"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent resize-none"
            placeholder="Tell us about your design and what you need..."
            required
            data-testid="design-description-input"
          />
        </div>

        {/* Dimensions */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Ruler className="inline h-4 w-4 mr-1" />
            Dimensions (optional)
          </label>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <input
                type="text"
                name="width"
                value={formData.width}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                placeholder="Width"
                data-testid="design-width-input"
              />
            </div>
            <div>
              <input
                type="text"
                name="height"
                value={formData.height}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                placeholder="Height"
                data-testid="design-height-input"
              />
            </div>
            <div>
              <select
                name="unit"
                value={formData.unit}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white"
                data-testid="design-unit-select"
              >
                <option value="mm">mm</option>
                <option value="cm">cm</option>
                <option value="inches">inches</option>
              </select>
            </div>
          </div>
        </div>

        {/* Quantity */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Package className="inline h-4 w-4 mr-1" />
            Quantity
          </label>
          <input
            type="number"
            name="quantity"
            value={formData.quantity}
            onChange={handleInputChange}
            min="1"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            data-testid="design-quantity-input"
          />
        </div>

        {/* Additional Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Additional Notes (optional)
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows={2}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent resize-none"
            placeholder="Any other details or special requirements..."
            data-testid="design-notes-input"
          />
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={uploading || !file}
          className="w-full rounded-full py-4 text-base font-medium"
          data-testid="design-submit-btn"
        >
          {uploading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-5 w-5 mr-2" />
              Submit Design for Quote
            </>
          )}
        </Button>

        <p className="text-xs text-center text-gray-500">
          We'll review your design and contact you with a quote within 24-48 hours
        </p>
      </form>
    </div>
  );
};

export default MyDesignUpload;
